import { Link } from "react-router-dom";
import { LineChart, Line, ResponsiveContainer, YAxis } from "recharts";
import { TrendingUp, TrendingDown, Sparkles } from "lucide-react";

const TYPE = {
  "shift-up": { Icon: TrendingUp, color: "#34d399", text: "text-emerald-300", border: "border-emerald-500/25 hover:border-emerald-400/60", label: "Surge" },
  "shift-down": { Icon: TrendingDown, color: "#fb7185", text: "text-rose-300", border: "border-rose-500/25 hover:border-rose-400/60", label: "Drop" },
  stalled: { Icon: Sparkles, color: "#a78bfa", text: "text-purple-300", border: "border-purple-500/25 hover:border-purple-400/60", label: "Stalled" },
};

const Track = ({ s, color }) => {
  const now = Math.round((s.currentProbability || 0) * 100);
  const from = s.initialProbability != null ? Math.round(s.initialProbability * 100) : null;
  const lo = from == null ? now : Math.min(from, now);
  const hi = from == null ? now : Math.max(from, now);
  return (
    <div className="w-full" data-testid="signal-track">
      <div className="relative h-2 rounded-full bg-white/[0.08]">
        <div className="absolute top-0 h-2 rounded-full opacity-70" style={{ left: `${lo}%`, width: `${Math.max(hi - lo, 0)}%`, background: color }} />
        {from != null && <span className="absolute -top-0.5 w-3 h-3 rounded-full border-2 border-white/40 bg-[#0b0b0f]" style={{ left: `calc(${from}% - 6px)` }} />}
        <span className="absolute -top-0.5 w-3 h-3 rounded-full shadow" style={{ left: `calc(${now}% - 6px)`, background: color }} />
      </div>
      <div className="flex justify-between text-[10px] font-mono text-white/40 mt-1">
        <span>0%</span>
        <span className="text-white/75">{from != null && from !== now ? `${from}% to ${now}%` : `${now}% pass probability`}</span>
        <span>100%</span>
      </div>
    </div>
  );
};

const Spark = ({ history, color }) => {
  if (!history || history.length < 2) return <div className="text-[10px] font-mono text-white/30 text-center">one snapshot</div>;
  return (
    <ResponsiveContainer width="100%" height={32}>
      <LineChart data={history} margin={{ top: 4, right: 2, bottom: 4, left: 2 }}>
        <YAxis domain={[0, 100]} hide />
        <Line type="monotone" dataKey="p" stroke={color} strokeWidth={2} dot={false} isAnimationActive={false} />
      </LineChart>
    </ResponsiveContainer>
  );
};

export const MovementRow = ({ s, index }) => {
  const t = TYPE[s.type] || TYPE.stalled;
  const delta = s.deltaPP;
  return (
    <Link to={`/bill/${s.billId}`} className={`block rounded-xl border whip-surface p-4 transition-colors ${t.border}`} data-testid={`signal-row-${index}`}>
      <div className="grid grid-cols-12 gap-3 items-center">
        <div className="col-span-12 md:col-span-5 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <t.Icon className={`w-3.5 h-3.5 ${t.text}`} />
            <span className={`text-[10px] font-mono font-bold uppercase tracking-[0.18em] ${t.text}`}>{t.label}</span>
            <span className="text-[10px] font-mono text-white/40">{s.chamber}</span>
            {s.policyArea && <span className="text-[10px] text-white/45 truncate">· {s.policyArea}</span>}
          </div>
          <p className="text-sm font-bold text-white leading-snug">
            <span className="font-mono text-orange-200 mr-2">{s.billNumber}</span>
            <span className="line-clamp-2">{s.billTitle || s.headline}</span>
          </p>
        </div>
        <div className="col-span-8 md:col-span-4"><Track s={s} color={t.color} /></div>
        <div className="col-span-2 md:col-span-2"><Spark history={s.history} color={t.color} /></div>
        <div className="col-span-2 md:col-span-1 text-right">
          {delta != null ? (
            <span className={`text-base font-black font-mono tabular-nums ${t.text}`} data-testid="signal-delta">{delta > 0 ? "+" : ""}{Math.round(delta)}</span>
          ) : (
            <span className="text-[11px] font-mono text-purple-300 leading-tight block">{s.daysSinceLastAction}d idle</span>
          )}
          {delta != null && <span className="block text-[9px] font-mono text-white/35 -mt-0.5">pp</span>}
        </div>
      </div>
      <div className="mt-3 flex flex-wrap items-center gap-1.5" data-testid="signal-drivers">
        <span className="text-[10px] font-mono uppercase tracking-wider text-white/35 mr-1">Why</span>
        {(s.drivers || []).map((d) => (
          <span key={d} className="text-[11px] px-2 py-0.5 rounded-full border border-white/10 bg-white/[0.03] text-white/70">{d}</span>
        ))}
        {s.lastActionDate && <span className="ml-auto text-[10px] font-mono text-white/35">last action {s.lastActionDate}</span>}
      </div>
    </Link>
  );
};
